import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { CityCard } from "./Cities";
import { priceLabel, DEAL_LABEL, Icon, Monogram, PropertyCard, Skeleton, CountUp, EmptyState, SectionHead, Badge } from "../ui";

export default function Landing() {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const heroRef = useRef(null);

  useEffect(() => { api.get("/property-desk/landing").then((r) => setData(r.data)).catch(() => setData({ hero: null, curated: [], counters: {}, insights: [] })); }, [api]);
  useEffect(() => {
    const f = () => { if (heroRef.current) heroRef.current.style.transform = `translateY(${window.scrollY * 0.35}px) scale(1.08)`; };
    window.addEventListener("scroll", f, { passive: true }); return () => window.removeEventListener("scroll", f);
  }, []);

  const hero = data?.hero;
  const heroImg = hero?.photos?.[0]?.url;
  return (
    <div data-testid="pd-landing">
      <section className="relative h-[100svh] min-h-[640px] overflow-hidden" data-testid="pd-hero">
        <div ref={heroRef} className="absolute inset-0 will-change-transform" style={{ transform: "scale(1.08)" }}>
          {heroImg ? <img src={heroImg} alt={hero.title} className="w-full h-full object-cover" /> : <Monogram />}
        </div>
        <div className="absolute inset-0 pd-hero-overlay" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0A0A0A]/60 via-transparent to-transparent" />
        <div className="absolute inset-x-0 bottom-0 pb-24 md:pb-28">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <div className="pd-label mb-6 pd-enter">Vinci Property Desk</div>
            <h1 className="pd-serif text-5xl sm:text-6xl lg:text-[88px] leading-[0.98] tracking-wide pd-enter-2">Exceptional Properties.<br /><span className="pd-gold font-light italic">Verified Network.</span></h1>
            <p className="pd-muted mt-7 text-base md:text-lg tracking-wide max-w-xl pd-enter-3">Exclusively for Vinci verified members and partners</p>
            <div className="mt-10 flex items-center gap-8 flex-wrap pd-enter-3">
              <Link to="/property-desk/explore" className="pd-cta" data-testid="pd-hero-cta">Explore Properties <Icon.Arrow size={14} /></Link>
              {hero && <Link to={`/property-desk/p/${hero.slug}`} className="pd-nav-link" data-testid="pd-hero-property-link">{hero.title} · {hero.city} →</Link>}
            </div>
          </div>
        </div>
        <div className="absolute bottom-8 inset-x-0 flex justify-center pd-gold pd-bob" data-testid="pd-scroll-indicator"><Icon.Down size={22} /></div>
      </section>

      <section className="pd-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHead label="Vinci Curated" title="A collection chosen by hand.">
            <Link to="/property-desk/curated" className="pd-nav-link" data-testid="pd-curated-all">View the portfolio →</Link>
          </SectionHead>
          {!data ? <div className="grid md:grid-cols-3 gap-8">{[0, 1, 2].map((i) => <Skeleton key={i} />)}</div>
            : data.curated.length === 0 ? <EmptyState text="Our curated collection grows by invitation. The first showcase properties arrive shortly." />
            : (
              <div className="flex gap-8 overflow-x-auto pd-hscroll -mx-6 px-6 md:-mx-10 md:px-10 snap-x" data-testid="pd-curated-row">
                {data.curated.map((p) => (
                  <Link key={p.id} to={`/property-desk/p/${p.slug}`} className="pd-card snap-start shrink-0 w-[82vw] sm:w-[460px] lg:w-[520px]" data-testid={`pd-curated-card-${p.id}`}>
                    <div className="pd-img-wrap aspect-[4/5] sm:aspect-[5/6]">
                      {p.photos?.[0]?.url ? <img src={p.photos[0].url} alt={p.title} /> : <Monogram />}
                      <div className="absolute inset-0 pd-hero-overlay" />
                      <div className="absolute top-5 right-5"><Badge kind="curated" /></div>
                      <div className="absolute inset-x-0 bottom-0 p-8">
                        <h3 className="pd-serif text-3xl tracking-wide leading-tight">{p.title}</h3>
                        <div className="mt-3 flex items-center justify-between gap-4 flex-wrap">
                          <span className="pd-muted text-xs tracking-[0.2em] uppercase">{p.city} · {DEAL_LABEL[p.deal_type]}</span>
                          <span className="pd-card-price pd-serif text-xl pd-gold">{priceLabel(p)}</span>
                        </div>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
        </div>
      </section>

      <section className="border-y pd-line pd-surface" data-testid="pd-counters">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 grid sm:grid-cols-3 gap-12 text-center sm:text-left">
          {[["Exceptional Properties", data?.counters?.properties], ["Cities Represented", data?.counters?.cities], ["States Covered", data?.counters?.states]].map(([l, n]) => (
            <div key={l}><div className="pd-serif text-6xl md:text-7xl pd-gold tabular-nums"><CountUp to={Number(n || 0)} /></div><div className="pd-label mt-4">{l}</div></div>
          ))}
        </div>
      </section>

      <CitiesRow api={api} />

      <section className="pd-section">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <SectionHead label="Discovery" title="Presently in the collection.">
            <Link to="/property-desk/explore" className="pd-cta !py-3 !px-6" data-testid="pd-discover-refine"><Icon.Sliders size={14} /> Refine</Link>
          </SectionHead>
          <DiscoveryPreview api={api} />
        </div>
      </section>

      {data?.insights?.length > 0 && (
        <section className="pd-section border-t pd-line">
          <div className="max-w-[1400px] mx-auto px-6 md:px-10">
            <SectionHead label="Market Insights" title="Intelligence from the desk."><Link to="/property-desk/insights" className="pd-nav-link">All insights →</Link></SectionHead>
            <div className="grid md:grid-cols-3 gap-8">
              {data.insights.map((a) => (
                <Link key={a.id} to={`/property-desk/insights/${a.slug}`} className="pd-card" data-testid={`pd-insight-card-${a.id}`}>
                  <div className="pd-img-wrap aspect-[16/10]">{a.cover_url ? <img src={a.cover_url} alt="" /> : <Monogram />}</div>
                  <div className="p-7"><div className="pd-label mb-3">{new Date(a.publish_date).toLocaleDateString("en-IN", { day: "2-digit", month: "long", year: "numeric" })}</div><h3 className="pd-serif text-2xl leading-tight">{a.title}</h3></div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}

function DiscoveryPreview({ api }) {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/property-desk/properties?limit=6").then((r) => setRows(r.data.properties)).catch(() => setRows([])); }, [api]);
  if (!rows) return <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">{[0, 1, 2].map((i) => <Skeleton key={i} portrait />)}</div>;
  if (!rows.length) return <EmptyState text="No exceptional properties match your criteria at this time. Our curated collection grows by invitation." />;
  return <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="pd-discovery-grid">{rows.map((p) => <PropertyCard key={p.id} p={p} />)}</div>;
}

function CitiesRow({ api }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { api.get("/property-desk/cities").then((r) => setRows(r.data.cities.slice(0, 6))).catch(() => {}); }, [api]);
  if (!rows.length) return null;
  return (
    <section className="pd-section border-b pd-line"><div className="max-w-[1400px] mx-auto px-6 md:px-10">
      <SectionHead label="City Collections" title="By city, by standard."><Link to="/property-desk/cities" className="pd-nav-link" data-testid="pd-cities-all">All cities →</Link></SectionHead>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-testid="pd-cities-row">{rows.map((c) => <CityCard key={c.slug} c={c} />)}</div>
    </div></section>
  );
}
